import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, Play } from "lucide-react";

export const AiCallSettingsCard = () => {
  const [c, setC] = useState(null);
  const [voices, setVoices] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/ai-calls/config").then((r) => setC(r.data));
    api.get("/admin/ai-calls/voices").then((r) => setVoices(r.data)).catch(() => {});
  }, []);
  if (!c) return null;
  const save = async () => {
    setSaving(true);
    try { setC((await api.put("/admin/ai-calls/config", { enabled: c.enabled, voice_id: c.voice_id, begin_message: c.begin_message, prompt: c.prompt })).data); toast.success("AI caller updated in Retell"); }
    catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };
  const preview = voices.find((v) => v.voice_id === c.voice_id)?.preview;
  return (
    <Card className="mb-5 p-6" data-testid="ai-call-settings">
      <div className="mb-3 flex items-center justify-between gap-4">
        <div><h3 className="font-display font-bold text-slate-900">AI Calling (Retell)</h3>
          <p className="text-xs text-slate-500">{c.agent_name ? `Editing your Retell agent "${c.agent_name}". ` : ""}Script and voice for AI prospect calls and AI Autopilot Mode. Variables: {"{{customer_name}} {{lead_type}} {{mlo_name}} {{mlo_first_name}} {{mlo_nmls}} {{property_address}} {{mlo_phone_number}}"}</p></div>
        <Switch checked={!!c.enabled} onCheckedChange={(v) => setC({ ...c, enabled: v })} data-testid="ai-call-enabled" />
      </div>
      <Label>Voice</Label>
      <div className="mt-1.5 flex gap-2">
        <Select value={c.voice_id} onValueChange={(v) => setC({ ...c, voice_id: v })}>
          <SelectTrigger data-testid="ai-voice-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">{(voices.length ? voices : [{ voice_id: c.voice_id, name: c.voice_id }]).map((v) => <SelectItem key={v.voice_id} value={v.voice_id}>{v.name} {v.gender ? `· ${v.gender}` : ""} {v.provider ? `· ${v.provider}` : ""}</SelectItem>)}</SelectContent>
        </Select>
        {preview && <Button variant="outline" size="icon" onClick={() => new Audio(preview).play()} data-testid="ai-voice-preview"><Play className="h-4 w-4" /></Button>}
      </div>
      <Label className="mt-4 block">Opening line</Label>
      <Textarea className="mt-1.5" rows={2} value={c.begin_message} onChange={(e) => setC({ ...c, begin_message: e.target.value })} data-testid="ai-begin-message" />
      <Label className="mt-4 block">Call script / instructions</Label>
      <Textarea className="mt-1.5 font-mono text-xs" rows={12} value={c.prompt} onChange={(e) => setC({ ...c, prompt: e.target.value })} data-testid="ai-prompt" />
      <Button onClick={save} disabled={saving} className="mt-4 bg-slate-900 hover:bg-slate-800" data-testid="ai-call-save">{saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save AI caller</Button>
    </Card>
  );
};
